import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import {
  applyBreakpointConfig,
  breakpointConfigOptions,
  breakpointOptionLabel,
  desktopConfigOptions,
  desktopOptionLabel,
} from 'src/helpers/columnsLayoutControls';
import type { BreakpointRowConfigValue, DesktopRowConfigValue } from 'src/helpers/columnsLayoutUtils';
import {
  hasOrphanAtAnyBreakpoint,
  readBreakpointRowConfig,
  readDesktopRowConfig,
} from 'src/helpers/columnsLayoutUtils';
import { IColumnsLayout, OrphanRowPolicy } from 'src/interfaces/PageColumn';

const toolbarControlSx = { flex: '0 1 140px', minWidth: 120 };

interface Props {
  layout: IColumnsLayout;
  onChange: (layout: IColumnsLayout) => void;
  showColumnCountTitle: boolean;
  showResponsiveControls: boolean;
  /** Legacy combined desktop dropdown (column count + wide center). */
  onDesktopConfigChange?: (value: DesktopRowConfigValue) => void;
  /** Modal mode: separate desktop column count selector. */
  showDesktopColumnCount?: boolean;
  desktopColumnCount?: 2 | 3 | 4;
  desktopColumnCountOptions?: (2 | 3 | 4)[];
  onDesktopColumnCountChange?: (count: 2 | 3 | 4) => void;
  onBreakpointConfigChange?: (bp: 'xs' | 'sm', value: BreakpointRowConfigValue) => void;
  readBreakpointConfig?: (bp: 'xs' | 'sm') => BreakpointRowConfigValue;
  breakpointConfigOptions?: BreakpointRowConfigValue[];
  breakpointOptionLabel?: (value: BreakpointRowConfigValue) => string;
}

export default function ColumnsLayoutToolbar({
  layout,
  onChange,
  showColumnCountTitle,
  showResponsiveControls,
  onDesktopConfigChange,
  showDesktopColumnCount = false,
  desktopColumnCount,
  desktopColumnCountOptions = [2, 3, 4],
  onDesktopColumnCountChange,
  onBreakpointConfigChange,
  readBreakpointConfig: readBreakpointConfigProp,
  breakpointConfigOptions: breakpointOptionsProp,
  breakpointOptionLabel: breakpointLabelProp,
}: Props) {
  const { t } = useTranslation();
  const n = layout.columns.length;
  const columnsPerRow = layout.columnsPerRow!;
  const showOrphanControls = hasOrphanAtAnyBreakpoint(n, columnsPerRow);
  const orphanHintSm =
    n === 3 && columnsPerRow.sm === 2
      ? t('system.pages.columnsPerRowOrphanHint', { n: 3 })
      : null;

  const bpOptions = breakpointOptionsProp ?? breakpointConfigOptions(n);
  const readBp = readBreakpointConfigProp ?? ((bp: 'xs' | 'sm') => readBreakpointRowConfig(layout, bp));
  const bpLabel =
    breakpointLabelProp ?? ((value: BreakpointRowConfigValue) => breakpointOptionLabel(value, t));

  const handleBreakpointChange = (bp: 'xs' | 'sm', value: BreakpointRowConfigValue) => {
    if (onBreakpointConfigChange) {
      onBreakpointConfigChange(bp, value);
      return;
    }
    onChange(applyBreakpointConfig(layout, bp, value));
  };

  return (
    <Box sx={{ mb: 2 }}>
      {showColumnCountTitle ? (
        <Typography
          variant="body2"
          sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}
        >
          {t('system.pages.changeColumnCount')}
        </Typography>
      ) : null}

      {showResponsiveControls ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'flex-start',
          }}
        >
          {showDesktopColumnCount && onDesktopColumnCountChange ? (
            <FormControl size="small" sx={toolbarControlSx}>
              <InputLabel>{t('system.pages.columnsDesktop')}</InputLabel>
              <Select
                label={t('system.pages.columnsDesktop')}
                value={desktopColumnCount ?? n}
                onChange={e =>
                  onDesktopColumnCountChange(Number(e.target.value) as 2 | 3 | 4)
                }
              >
                {desktopColumnCountOptions.map(count => (
                  <MenuItem key={count} value={count}>
                    {t('system.pages.columnsDesktopCount', { count })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : onDesktopConfigChange ? (
            <FormControl size="small" sx={toolbarControlSx}>
              <InputLabel>{t('system.pages.columnsDesktop')}</InputLabel>
              <Select
                label={t('system.pages.columnsDesktop')}
                value={readDesktopRowConfig(layout)}
                onChange={e =>
                  onDesktopConfigChange(e.target.value as DesktopRowConfigValue)
                }
              >
                {desktopConfigOptions().map(value => (
                  <MenuItem key={value} value={value}>
                    {desktopOptionLabel(value, t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          <FormControl size="small" sx={toolbarControlSx}>
            <InputLabel>{t('system.pages.columnsTablet')}</InputLabel>
            <Select
              label={t('system.pages.columnsTablet')}
              value={readBp('sm')}
              onChange={e => handleBreakpointChange('sm', e.target.value as BreakpointRowConfigValue)}
            >
              {bpOptions.map(value => (
                <MenuItem key={value} value={value}>
                  {bpLabel(value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={toolbarControlSx}>
            <InputLabel>{t('system.pages.columnsMobile')}</InputLabel>
            <Select
              label={t('system.pages.columnsMobile')}
              value={readBp('xs')}
              onChange={e => handleBreakpointChange('xs', e.target.value as BreakpointRowConfigValue)}
            >
              {bpOptions.map(value => (
                <MenuItem key={value} value={value}>
                  {bpLabel(value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showOrphanControls ? (
            <FormControl size="small" sx={toolbarControlSx}>
              <InputLabel>{t('system.pages.orphanRowPolicy.label')}</InputLabel>
              <Select
                label={t('system.pages.orphanRowPolicy.label')}
                value={layout.orphanRowPolicy ?? 'natural'}
                onChange={e =>
                  onChange({
                    ...layout,
                    orphanRowPolicy: e.target.value as OrphanRowPolicy,
                  })
                }
              >
                <MenuItem value="natural">{t('system.pages.orphanRowPolicy.natural')}</MenuItem>
                <MenuItem value="fullWidth">{t('system.pages.orphanRowPolicy.fullWidth')}</MenuItem>
                <MenuItem value="centered">{t('system.pages.orphanRowPolicy.centered')}</MenuItem>
              </Select>
            </FormControl>
          ) : null}
        </Box>
      ) : null}

      {showResponsiveControls && orphanHintSm ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {orphanHintSm}
        </Typography>
      ) : null}
    </Box>
  );
}
